// main imports
import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { FormattedMessage } from "react-intl";

// models
import { ILineData, ILineGroupData, IPlantData } from "@/lib/backend/manufacturing2.generated.types";

// backend
import { getBackend } from "@/lib/backend/backend2";
import {
    waitForManufacturingAsync,
    getPlantsForUser,
    getLineGroupsForUser,
    filterLinesForUser,
    forceManufacturingCacheRefresh,
} from "@/lib/businessLogic";
import * as Auth from "@/lib/auth";
import { parseUrlQueryVariables } from "@/lib/util";

// import subcomponents
import LinesList from "./LinesList";
import LinesDetails from "./LinesDetails";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import * as Redirect from "@/app/URLHandler/redirect";

import { ResourcesLayout } from "@/modules/shared/MainContent";
import { ReduxState } from "@/store/reducers";

type OwnProps = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};

type Props = OwnProps & ReduxProps;

type State = {
    lines: ILineData[];
    line_uuid_to_group_titles: Map<string, string[]>;
    plants: IPlantData[];
    plant_uuid: string;
    error: string;
    warning: string;
    selected: ILineData | null;
    searchValue: string;
};

/**
 * Displaying production lines list.
 */
class LinesComponent extends Component<Props, State> {
    linesListRef = React.createRef<LinesList>();
    searchDebounceTimer?: ReturnType<typeof setTimeout>;

    state: State = {
        lines: [],
        line_uuid_to_group_titles: new Map(),
        plants: [],
        plant_uuid: "",
        error: "",
        warning: "",
        selected: null,
        searchValue: "",
    };

    async loadComponent() {
        try {
            await waitForManufacturingAsync();
            const plants: IPlantData[] = getPlantsForUser();
            const line_groups: ILineGroupData[] = getLineGroupsForUser();
            let lines: ILineData[] = filterLinesForUser(
                (await getBackend().manufacturing.getLines({ only_parent: true })).lines,
                true
            );
            // collect plant weights
            const plant_weights: Map<string, number> = new Map();
            plants.forEach(x => {
                plant_weights.set(x.uuid, x.weight);
            });

            // collect lingroup weights
            const linegroup_weights: Map<string, number> = new Map();
            // line => linegroup mapping
            const line_linegroup: Map<string, string> = new Map();
            line_groups.forEach(x => {
                const lines1 = x.line_uuids;
                lines1.forEach(x1 => {
                    line_linegroup.set(x1, x.uuid);
                });
                linegroup_weights.set(x.uuid, x.weight);
            });

            if (this.props.resource_edit_filters.plant_uuid) {
                lines = lines.filter(l => l.plant_uuid === this.props.resource_edit_filters.plant_uuid);
            }

            // add plant weight and linegroup weight to each line
            const line_linegroup_weights: Map<string, number> = new Map();
            const line_plant_weights: Map<string, number> = new Map();
            lines.forEach(x => {
                if (line_linegroup.has(x.uuid)) {
                    const linegroup_uuid = line_linegroup.get(x.uuid) || "";
                    if (linegroup_weights.has(linegroup_uuid)) {
                        const line_linegroup_weight = linegroup_weights.get(linegroup_uuid);
                        if (typeof line_linegroup_weight !== "undefined") {
                            line_linegroup_weights.set(x.uuid, line_linegroup_weight);
                        }
                    }
                }
                if (plant_weights.has(x.plant_uuid)) {
                    const line_plant_weight = plant_weights.get(x.plant_uuid);
                    if (typeof line_plant_weight !== "undefined") {
                        line_plant_weights.set(x.uuid, line_plant_weight);
                    }
                }
            });
            // sort lines  by plant, linegroup, line weight
            lines.sort((a, b) => {
                const a_plant_weight = line_plant_weights.get(a.uuid) ?? Number.MAX_SAFE_INTEGER;
                const b_plant_weight = line_plant_weights.get(b.uuid) ?? Number.MAX_SAFE_INTEGER;
                const a_linegroup_weight = line_linegroup_weights.get(a.uuid) ?? Number.MAX_SAFE_INTEGER;
                const b_linegroup_weight = line_linegroup_weights.get(b.uuid) ?? Number.MAX_SAFE_INTEGER;
                if (a_plant_weight > b_plant_weight) {
                    return 1;
                }
                if (a_plant_weight < b_plant_weight) {
                    return -1;
                }
                if (a_linegroup_weight > b_linegroup_weight) {
                    return 1;
                }
                if (a_linegroup_weight < b_linegroup_weight) {
                    return -1;
                }
                if (a.weight > b.weight) {
                    return 1;
                }
                if (a.weight < b.weight) {
                    return -1;
                }
                return 1;
            });

            let selected: ILineData | null = null;
            // Select initial line.
            if (this.props.match.params.uuid) {
                const filtered = lines.filter(line => line.uuid === this.props.match.params.uuid);
                if (filtered.length) {
                    selected = filtered[0];
                }
            } else if (this.props.resource_edit_filters.lines_title_filter) {
                const filtered = lines.filter(
                    line =>
                        line.title
                            .toLowerCase()
                            .includes(this.props.resource_edit_filters.lines_title_filter.toLowerCase()) ||
                        line.external_id
                            .toLowerCase()
                            .includes(this.props.resource_edit_filters.lines_title_filter.toLowerCase())
                );

                if (filtered.length) {
                    selected = filtered[0];
                }
            } else if (lines.length) {
                selected = lines[0];
            }

            // prepare map from line to its linegroups (can be more!)
            const line_uuid_to_group_titles: Map<string, string[]> = new Map();
            for (const line_group of line_groups) {
                for (const line_uuid of line_group.line_uuids) {
                    const group_titles = line_uuid_to_group_titles.get(line_uuid);
                    if (group_titles !== undefined) {
                        group_titles.push(line_group.title);
                        line_uuid_to_group_titles.set(line_uuid, group_titles);
                    } else {
                        line_uuid_to_group_titles.set(line_uuid, [line_group.title]);
                    }
                }
            }

            const url_params = parseUrlQueryVariables(this.props.location.search);
            const plant_uuid = url_params.plant_uuid !== undefined ? url_params.plant_uuid : "";

            this.setState({
                lines,
                line_uuid_to_group_titles,
                plants,
                plant_uuid,
                selected,
                error: "",
                warning: "",
            });
        } catch (e) {
            this.setState({ error: (e as Error).message });
        }
    }

    async componentDidMount() {
        // Initialize search value from Redux state
        this.setState({
            searchValue: this.props.resource_edit_filters.lines_title_filter || "",
        });
        await this.loadComponent();
    }

    async componentDidUpdate(prevProps: Props) {
        if (prevProps.resource_edit_filters.plant_uuid !== this.props.resource_edit_filters.plant_uuid) {
            await this.loadComponent();
        }

        // Sync local search value with Redux state when it changes externally
        const current_filter = this.props.resource_edit_filters.lines_title_filter || "";
        const prev_filter = prevProps.resource_edit_filters.lines_title_filter || "";
        if (current_filter !== prev_filter && current_filter !== this.state.searchValue) {
            this.setState({
                searchValue: current_filter,
                selected: this.selectLine(current_filter, this.props.resource_edit_filters.plant_uuid),
            });
        }
    }

    componentWillUnmount() {
        clearTimeout(this.searchDebounceTimer);
    }
    /**
     * Deleting a selected line.
     *
     * @param {string} uuid Line universal unique identifier.
     */
    handleToDelete = async (uuid: string) => {
        try {
            await getBackend().manufacturing.deleteLine({ line_uuid: uuid });
            await forceManufacturingCacheRefresh();
            this.handleToUpdate();
        } catch (err) {
            this.handleToError((err as Error).message);
        }
    };

    /**
     * Select the first line that matches all the given criteria.
     * @param {string} contentFilter Line search term.
     * @param {string} plantUuid Plant UUID.
     * @param {ILineData[]} [lines] List of lines to select from.
     */
    selectLine(contentFilter: string, plantUuid: string, lines: ILineData[] = this.state.lines): ILineData | null {
        for (const line of lines) {
            const visible = this.linesListRef.current && this.linesListRef.current.includes(contentFilter, line);

            if ((plantUuid === line.plant_uuid || plantUuid === "") && visible) {
                return line;
            }
        }

        return null;
    }

    /**
     * Debounced search function that updates Redux state and URL
     */
    debouncedSearch = (value: string) => {
        const selected = this.selectLine(value, this.props.resource_edit_filters.plant_uuid);

        this.setState({ selected });

        Redirect.resourceEditFilters(this.props.history, "/digital-twin/resources/lines", {
            ...this.props.resource_edit_filters,
            lines_title_filter: value,
        });
    };

    /**
     * Handling change in search input - uses local state to prevent cursor jumping
     * and debounces the actual search to reduce API calls
     */
    handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;

        // Update local state immediately for responsive UI
        this.setState({
            searchValue: value,
        });

        // Clear existing debounce timer
        clearTimeout(this.searchDebounceTimer);

        // Set new debounce timer (300ms delay)
        this.searchDebounceTimer = setTimeout(() => {
            this.debouncedSearch(value);
        }, 300);
    };

    /**
     * Handling change in any input component (non-search inputs).
     */
    handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // read new values
        const target = event.currentTarget;
        const value = target.value;
        const name = target.name;

        if (name === "plant_uuid") {
            const plantUuid = value;
            const line = this.state.lines.filter(line => line.plant_uuid === plantUuid);
            const line_uuid = line.length > 0 ? line[0].uuid : "";

            Redirect.resourceEditFilters(this.props.history, "/digital-twin/resources/lines/" + line_uuid, {
                ...this.props.resource_edit_filters,
                plant_uuid: plantUuid,
            });

            this.setState({
                selected: this.selectLine(this.state.searchValue, plantUuid),
            });
        }
    };

    /**
     * Handle `click` events for line items.
     */
    handleLineClick(line: ILineData) {
        this.setState({ selected: line });

        // Update URL.
        this.props.history.push(`/digital-twin/resources/lines/${line.uuid}`);
    }

    /**
     * Handle, that can be called by the children, to update the data.
     */
    handleToUpdate = () => {
        this.loadComponent();
    };

    /**
     * Handling error from subordinate component.
     */
    handleToError = (str: string) => {
        this.setState({ error: str });
    };

    render() {
        const can_edit = Auth.isInElevatedRole();
        return (
            <ResourcesLayout>
                <ResourcesLayout.Main>
                    <ResourcesLayout.List>
                        <ResourcesLayout.Search>
                            <div className="form-group form-group-stretch">
                                <input
                                    type="text"
                                    className="form-control search_bar"
                                    placeholder={translate("common.search", "Search") + " ..."}
                                    name="title_filter"
                                    value={this.state.searchValue}
                                    onChange={this.handleSearchInputChange}
                                />
                            </div>
                            <div className="form-group">
                                <select
                                    name="plant_uuid"
                                    className="form-control select_filter_control"
                                    value={this.props.resource_edit_filters.plant_uuid}
                                    onChange={this.handleInputChange}
                                >
                                    <option key={-1} value="">
                                        {translate("Manufacturing.Lines.all_plants", "All Plants")}
                                    </option>
                                    {this.state.plants.map((plant, i) => {
                                        return (
                                            <option key={i} value={plant.uuid}>
                                                {plant.title}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            {can_edit && (
                                <div className="form-group">
                                    <Link
                                        to="/digital-twin/resources/line/create"
                                        className="btn btn-inv-primary btn-icon btn-icon-add"
                                        title={translate("Manufacturing.Lines.add_new_line", "Add new line")}
                                    >
                                        <span className="sr-only">
                                            <FormattedMessage
                                                id="Manufacturing.Lines.add_new_line"
                                                defaultMessage="Add new line"
                                            />
                                        </span>
                                    </Link>
                                </div>
                            )}
                        </ResourcesLayout.Search>
                        <LinesList
                            ref={this.linesListRef}
                            lines={this.state.lines}
                            selected={this.state.selected}
                            filter={this.state.searchValue}
                            plantFilter={this.props.resource_edit_filters.plant_uuid}
                            line_uuid_to_group_titles={this.state.line_uuid_to_group_titles}
                            handleLineClick={line => {
                                this.handleLineClick(line);
                            }}
                        />
                    </ResourcesLayout.List>
                    <ResourcesLayout.Content>
                        <ErrorComponent msg={this.state.error} type="error" />
                        <ErrorComponent msg={this.state.warning} type="warning" />
                        {this.state.selected && (
                            <LinesDetails
                                selected={this.state.selected}
                                editLink="/digital-twin/resources/line/edit/"
                                kanbanLink="/digital-twin/resources/line/kanban/"
                                handleToDelete={this.handleToDelete}
                                reloadComponent={() => {
                                    this.loadComponent();
                                }}
                            />
                        )}
                    </ResourcesLayout.Content>
                </ResourcesLayout.Main>
            </ResourcesLayout>
        );
    }
}

const mapState = (state: ReduxState) => {
    return { resource_edit_filters: state.resource_edit_filters };
};

type ReduxProps = ReturnType<typeof mapState>;

export default connect<ReduxProps, {}, OwnProps, ReduxState>(mapState)(LinesComponent);
